import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useRef, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { MemberCreateDto } from "../../types/device";
import { checkUserExists } from "../../api/admin";

interface MemberFormValues {
  name: string;
  email: string;
  access: "Admin" | "User";
  password: string;
}

interface ExistingUserState {
  exists: boolean;
  name: string | null;
  alreadyMember: boolean;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim(),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  access: Yup.string()
    .oneOf(["Admin", "User"], "Access level must be Admin or User")
    .required("Access level is required"),
  password: Yup.string().test(
    "password-length",
    "Password must be at least 8 characters",
    (value) => !value || value.length >= 8,
  ),
});

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (memberData: MemberCreateDto) => void;
  isLoading?: boolean;
}

export default function AddMemberModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: AddMemberModalProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [existing, setExisting] = useState<ExistingUserState | null>(null);
  const [checking, setChecking] = useState(false);
  const lastCheckedEmail = useRef("");

  const initialValues: MemberFormValues = {
    name: "",
    email: "",
    access: "User",
    password: "",
  };

  // Look up the email on the platform once it's a valid address — existing
  // users join the org with their current account, so no name/password input
  const checkEmail = async (email: string) => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setExisting(null);
      lastCheckedEmail.current = "";
      return;
    }
    if (trimmed === lastCheckedEmail.current) return;
    lastCheckedEmail.current = trimmed;
    setChecking(true);
    try {
      const res = await checkUserExists(trimmed);
      setExisting(res.data);
    } catch {
      setExisting(null);
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = (
    values: MemberFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<MemberFormValues>,
  ) => {
    const trimmedPassword = values.password.trim();
    if (!existing?.exists && !values.name.trim()) {
      setFieldError("name", "Name is required");
      setSubmitting(false);
      return;
    }
    onSubmit({
      name: existing?.exists ? (existing.name ?? values.email) : values.name.trim(),
      email: values.email.trim(),
      access: values.access,
      ...(!existing?.exists && trimmedPassword ? { password: trimmedPassword } : {}),
    });
  };

  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                      <UserPlusIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                      Add New Member
                    </h3>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting, handleBlur }) => (
                    <Form className="space-y-4">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          Email Address *
                        </label>
                        <Field
                          type="email"
                          name="email"
                          id="email"
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                          placeholder="Enter email address"
                          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                            handleBlur(e);
                            checkEmail(e.target.value);
                          }}
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                        {checking && (
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Checking email...
                          </p>
                        )}
                      </div>

                      {/* Existing user notice */}
                      {existing?.exists && existing.alreadyMember && (
                        <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3">
                          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-amber-800 dark:text-amber-300">
                            <strong>{existing.name}</strong> is already a member of this
                            organisation.
                          </p>
                        </div>
                      )}
                      {existing?.exists && !existing.alreadyMember && (
                        <div className="flex items-start gap-2 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
                          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-green-800 dark:text-green-300">
                            <strong>{existing.name}</strong> already has an OsmoAir account.
                            They'll be added to this organisation and keep their existing
                            password.
                          </p>
                        </div>
                      )}

                      {/* Name + password only apply to brand-new accounts */}
                      {!existing?.exists && (
                        <div>
                          <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                          >
                            Full Name *
                          </label>
                          <Field
                            type="text"
                            name="name"
                            id="name"
                            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                            placeholder="Enter full name"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      )}

                      <div>
                        <label
                          htmlFor="access"
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          Access Level (this organisation) *
                        </label>
                        <Field
                          as="select"
                          name="access"
                          id="access"
                          className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                        >
                          <option value="User">User</option>
                          <option value="Admin">Admin</option>
                        </Field>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Roles apply per organisation — the same user can be Admin in one
                          organisation and User in another.
                        </p>
                        <ErrorMessage
                          name="access"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />
                      </div>

                      {!existing?.exists && (
                        <div>
                          <label
                            htmlFor="password"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                          >
                            Password
                          </label>
                          <div className="relative">
                            <Field
                              type={showPassword ? "text" : "password"}
                              name="password"
                              id="password"
                              autoComplete="new-password"
                              className="block w-full pr-10 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white sm:text-sm"
                              placeholder="Leave blank to auto-generate"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((s) => !s)}
                              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                              tabIndex={-1}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                              {showPassword ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            If left blank, a password is generated and emailed to them.
                          </p>
                          <ErrorMessage
                            name="password"
                            component="div"
                            className="mt-1 text-sm text-red-600"
                          />
                        </div>
                      )}

                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                          disabled={isSubmitting || isLoading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="inline-flex justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            isSubmitting ||
                            isLoading ||
                            checking ||
                            (existing?.exists && existing.alreadyMember)
                          }
                        >
                          {isSubmitting || isLoading
                            ? "Adding..."
                            : existing?.exists
                              ? "Add to Organisation"
                              : "Add Member"}
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
